import { useState } from "react";
import { Badge, Button, Dialog, Field, Icon, IconButton, Input, Select, Switch } from "../../ds/DS";
import { api } from "../../lib/api";
import type { VCredential } from "../data";
import type { PageProps } from "../Portal";
import { DialogFoot, PageHead } from "./parts";
import { firstName, plural } from "./util";
import { NewTicket } from "./ticketParts";

const ACCOUNT = "Client account";
const CRED_TYPES = ["API key", "Secret", "Password", "Token", "Access key", "Other"];
const ENVS = ["Production", "Sandbox", "Staging"];

function CredentialDialog({
  clientId,
  projects,
  defaultProjectId,
  contact,
  cred,
  onClose,
  onSaved,
  onDelete,
}: {
  clientId: string;
  projects: PageProps["projects"];
  defaultProjectId: string | null;
  contact: string;
  cred: VCredential | null;
  onClose: () => void;
  onSaved: () => void;
  onDelete: (c: VCredential) => void;
}) {
  const scopeName = (id: string | null) =>
    id ? (projects.find((p) => p.id === id)?.name ?? ACCOUNT) : ACCOUNT;
  const [f, setF] = useState(() => ({
    label: cred?.label ?? "",
    env: cred?.env || "Production",
    type: cred?.type || "API key",
    scope: scopeName(cred ? cred.projectId : defaultProjectId),
    username: cred?.username ?? "",
    secret: "",
    url: cred?.url ?? "",
    clientVisible: cred ? cred.clientVisible : true,
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));
  const envOptions = ENVS.includes(f.env) ? ENVS : [...ENVS, f.env];
  const typeOptions = CRED_TYPES.includes(f.type) ? CRED_TYPES : [...CRED_TYPES, f.type];
  const invalid = !f.label.trim() || (!cred && !f.secret);

  const save = async () => {
    if (invalid) return;
    setBusy(true);
    setErr("");
    const projectId =
      f.scope === ACCOUNT ? null : (projects.find((p) => p.name === f.scope)?.id ?? null);
    try {
      if (cred) {
        await api.patch(`/credentials/${cred.id}`, {
          label: f.label.trim(),
          username: f.username,
          env: f.env,
          type: f.type,
          url: f.url,
          clientVisible: f.clientVisible,
          ...(f.secret ? { secret: f.secret } : {}),
        });
      } else {
        await api.post("/credentials", {
          clientId,
          projectId,
          label: f.label.trim(),
          username: f.username,
          secret: f.secret,
          env: f.env,
          type: f.type,
          url: f.url,
          clientVisible: f.clientVisible,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save the entry. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title={cred ? "Rotate or edit" : "Add entry"}
      description="Encrypted at rest. Every reveal and copy is logged in Activity."
      accent
      width={560}
      onClose={onClose}
      footer={
        <DialogFoot
          saveLabel="Save"
          busy={busy}
          invalid={invalid}
          onCancel={onClose}
          onSave={() => void save()}
          deleteLabel={cred ? "Delete entry" : undefined}
          deleteWhat={cred ? `“${cred.label}”` : undefined}
          onDelete={cred ? () => onDelete(cred) : undefined}
        />
      }
    >
      <div className="stack-16">
        {err ? <div className="form-err">{err}</div> : null}
        <Field label="Label" required>
          <Input value={f.label} onChange={(e) => set("label", e.target.value)} />
        </Field>
        <div className="grid-2">
          <Field label="Type">
            <Select options={typeOptions} value={f.type} onChange={(e) => set("type", e.target.value)} />
          </Field>
          <Field label="Environment">
            <Select options={envOptions} value={f.env} onChange={(e) => set("env", e.target.value)} />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Belongs to">
            <Select
              options={[ACCOUNT, ...projects.map((p) => p.name)]}
              value={f.scope}
              disabled={!!cred}
              onChange={(e) => set("scope", e.target.value)}
            />
          </Field>
          <Field label="Username">
            <Input value={f.username} onChange={(e) => set("username", e.target.value)} />
          </Field>
        </div>
        <Field
          label={cred ? "New secret" : "Secret"}
          required={!cred}
          hint={cred ? "Leave blank to keep the current secret." : undefined}
        >
          <Input
            type="password"
            value={f.secret}
            onChange={(e) => set("secret", e.target.value)}
            autoComplete="new-password"
          />
        </Field>
        <Field label="URL">
          <Input
            value={f.url}
            onChange={(e) => set("url", e.target.value)}
            placeholder="admin.example.com"
          />
        </Field>
        <div className="ds-field">
          <span className="ds-field-label">Visible to client</span>
          <div className="row-8">
            <Switch
              checked={f.clientVisible}
              onChange={(v) => setF((s) => ({ ...s, clientVisible: v }))}
              label="Visible to client"
            />
            <span className="hint">
              {f.clientVisible
                ? `${contact} can see and reveal this entry.`
                : "Agile Software only."}
            </span>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

// Masked secret: click to reveal (logged), copy button (logged as a copy).
function SecretCell({ cred, toast }: { cred: VCredential; toast: (m: string) => void }) {
  const [value, setValue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reveal = async () => {
    if (value !== null) {
      setValue(null);
      return;
    }
    setBusy(true);
    try {
      const { secret } = await api.post<{ secret: string }>(`/credentials/${cred.id}/reveal`);
      setValue(secret);
    } catch {
      toast("Couldn't reveal it. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      const { secret } = await api.post<{ secret: string }>(`/credentials/${cred.id}/reveal`, {
        copy: true,
      });
      await navigator.clipboard.writeText(secret);
      toast("Secret copied.");
    } catch {
      toast("Couldn't copy. Reveal it and copy by hand.");
    }
  };

  return (
    <div className="vrow-secret">
      <button
        type="button"
        className={"secret" + (value !== null ? " is-on" : "")}
        onClick={() => void reveal()}
      >
        <span className="secret-val mono">
          {busy ? "…" : value !== null ? value : "••••••••••••••"}
        </span>
        <Icon name={value !== null ? "eye-off" : "eye"} size={14} />
      </button>
      <IconButton icon="copy" label="Copy secret" variant="ghost" onClick={() => void copy()} />
    </div>
  );
}

export function Vault(props: PageProps) {
  return props.isAdmin ? <AdminVault {...props} /> : <ClientLogins {...props} />;
}

/* ---------- client Logins (handoff C4 `isCLogins`) ---------- */

// Masked value with Show/Hide and Copy; both go through /reveal, which logs
// the reveal (or the copy) to Activity.
function LoginSecret({ cred, toast }: { cred: VCredential; toast: (m: string) => void }) {
  const [value, setValue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (value !== null) {
      setValue(null);
      return;
    }
    setBusy(true);
    try {
      const { secret } = await api.post<{ secret: string }>(`/credentials/${cred.id}/reveal`);
      setValue(secret);
    } catch {
      toast("Couldn't show it. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    try {
      const { secret } = await api.post<{ secret: string }>(`/credentials/${cred.id}/reveal`, {
        copy: true,
      });
      await navigator.clipboard.writeText(secret);
      toast("Copied.");
    } catch {
      toast("Couldn't copy. Click Show and copy it by hand.");
    }
  };

  return (
    <div className="cl-secret">
      <span className="cl-secret-val mono">
        {busy ? "…" : value !== null ? value : "••••••••••••"}
      </span>
      <Button
        variant="secondary"
        size="sm"
        icon={value !== null ? "eye-off" : "eye"}
        disabled={busy}
        onClick={() => void toggle()}
      >
        {value !== null ? "Hide" : "Show"}
      </Button>
      <IconButton icon="copy" label="Copy" variant="ghost" onClick={() => void copy()} />
    </div>
  );
}

function ClientLogins({ projects, credentials, pid, reload, toast, openItem }: PageProps) {
  const [asking, setAsking] = useState(false);
  const rows = credentials.filter((c) => c.clientVisible);
  const scopeName = (id: string | null) =>
    id ? (projects.find((p) => p.id === id)?.name ?? "Your account") : "Your account";
  return (
    <div className="stack-24">
      <PageHead
        eyebrow="LOGINS"
        title="Logins"
        sub="Logins we keep for your projects. Hidden until you click Show."
      />
      <div className="list-card">
        {rows.length === 0 ? (
          <div className="list-empty">No logins here yet.</div>
        ) : (
          rows.map((c) => (
            <div key={c.id} className="cl-row">
              <div style={{ minWidth: 0 }}>
                <div className="row-8">
                  <span className="cl-label">{c.label}</span>
                  {c.env && c.env !== "Production" ? <Badge tone="neutral">{c.env}</Badge> : null}
                </div>
                <div className="cl-sub">{[scopeName(c.projectId), c.url].filter(Boolean).join(" · ")}</div>
                {c.username ? <div className="cl-user mono">{c.username}</div> : null}
              </div>
              <LoginSecret cred={c} toast={toast} />
            </div>
          ))
        )}
      </div>
      <div className="cl-ask">
        Need a login added or changed?{" "}
        <button type="button" className="link-btn" onClick={() => setAsking(true)}>
          Ask us →
        </button>
      </div>
      <NewTicket
        open={asking}
        isAdmin={false}
        projects={projects}
        defaultPid={pid}
        onClose={() => setAsking(false)}
        onCreated={(id) => {
          reload();
          toast("Ticket raised. You'll get a reply within one working day.");
          openItem("tickets", id);
        }}
      />
    </div>
  );
}

/* ---------- admin Vault ---------- */

function AdminVault({
  client,
  projects,
  credentials,
  pid,
  reload,
  toast,
}: PageProps) {
  const inProject = pid !== "all";
  const [dialog, setDialog] = useState<VCredential | null | "new">(null);
  const contact = client?.contact ? firstName(client.contact) : "The client";

  const del = async (cred: VCredential) => {
    await api.del(`/credentials/${cred.id}`);
    setDialog(null);
    reload();
    toast("Entry deleted.");
  };

  // Client account first, then one group per project (just the current
  // project when scoped to one).
  const scopes: (string | null)[] = inProject ? [pid] : [null, ...projects.map((p) => p.id)];
  const groups = scopes
    .map((sc) => {
      const project = sc ? projects.find((p) => p.id === sc) : undefined;
      return {
        key: sc ?? "account",
        heading: project?.name ?? ACCOUNT,
        last: project
          ? `Only people with access to ${project.name} can see these.`
          : "Shared across every project on this account.",
        rows: credentials.filter((c) => c.projectId === sc),
      };
    })
    .filter((g) => g.rows.length);

  return (
    <div className="stack-24">
      <PageHead
        eyebrow="CREDENTIALS"
        title="Vault"
        sub={`Keys, logins and secrets for ${client?.name ?? "this client"}. Encrypted at rest, revealed only on request.`}
        actions={
          <Button icon="plus" onClick={() => setDialog("new")}>
            Add entry
          </Button>
        }
      />
      <div className="page-note">
        <Icon name="lock" size={15} />
        Encrypted at rest. Every reveal and copy is logged in Activity, and the client can see who
        did it.
      </div>

      {groups.length === 0 ? (
        <div className="list-card">
          <div className="list-empty">No credentials here yet.</div>
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.key} className="list-card">
            <div className="list-card-head">
              <span className="list-card-title">{g.heading}</span>
              <span className="mono list-card-count">
                {plural(g.rows.length, "entry", "entries")}
              </span>
            </div>
            <div className="grid-row grid-head vault-cols">
              <span>Entry</span>
              <span>Environment</span>
              <span>Username</span>
              <span>Secret</span>
              <span>Updated</span>
              <span />
            </div>
            {g.rows.map((cr) => (
              <div key={cr.id} className="grid-row vault-cols">
                <div className="cell-min">
                  <div className="row-8">
                    <span className="v-label">{cr.label}</span>
                    {!cr.clientVisible ? <Badge tone="warning">Hidden</Badge> : null}
                  </div>
                  <div className="cell-sub mono">
                    {[cr.type, cr.url].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <span data-label="Environment">
                  {cr.env ? (
                    <Badge tone={cr.env === "Production" ? "navy" : "neutral"}>{cr.env}</Badge>
                  ) : (
                    "—"
                  )}
                </span>
                <span className="cell-user mono" data-label="Username">
                  {cr.username || "—"}
                </span>
                <div className="cell-min" data-label="Secret">
                  <SecretCell cred={cr} toast={toast} />
                </div>
                <span className="cell-date mono" data-label="Updated">
                  {cr.updated}
                </span>
                <div className="row-6 end">
                  <IconButton
                    icon="rotate-cw"
                    label="Rotate or edit"
                    variant="ghost"
                    onClick={() => setDialog(cr)}
                  />
                  <IconButton
                    icon="trash-2"
                    label="Delete"
                    variant="ghost"
                    onClick={() => void del(cr)}
                  />
                </div>
              </div>
            ))}
            <div className="list-card-foot">{g.last}</div>
          </div>
        ))
      )}

      {dialog !== null && client ? (
        <CredentialDialog
          clientId={client.id}
          projects={projects}
          defaultProjectId={inProject ? pid : null}
          contact={contact}
          cred={dialog === "new" ? null : dialog}
          onClose={() => setDialog(null)}
          onDelete={(c) => void del(c)}
          onSaved={() => {
            reload();
            toast(dialog === "new" ? "Saved to the vault." : "Entry updated.");
          }}
        />
      ) : null}
    </div>
  );
}
